/**
 * Security utility functions for input validation and sanitization
 */

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input - The input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove any HTML tags and potentially dangerous characters
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid URL
 */
export const validateURL = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validate phone number (basic format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid phone format
 */
export const validatePhone = (phone) => {
  const phoneRegex = /^[\d\s\-\+\(\)]{8,20}$/;
  return phoneRegex.test(phone);
};

/**
 * Sanitize object by sanitizing all string values
 * @param {Object} obj - Object to sanitize
 * @returns {Object} - Sanitized object
 */
export const sanitizeObject = (obj) => {
  if (typeof obj !== 'object' || obj === null) return obj;
  
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

/**
 * Rate limit check using localStorage (client-side)
 * @param {string} action - Action identifier
 * @param {number} limit - Maximum attempts
 * @param {number} windowMs - Time window in milliseconds
 * @returns {boolean} - True if within rate limit
 */
export const checkRateLimit = (action, limit = 5, windowMs = 60000) => {
  const key = `rateLimit_${action}`;
  const now = Date.now();
  
  try {
    const data = JSON.parse(localStorage.getItem(key) || '[]');
    const recentAttempts = data.filter(timestamp => now - timestamp < windowMs);
    
    if (recentAttempts.length >= limit) {
      return false;
    }
    
    recentAttempts.push(now);
    localStorage.setItem(key, JSON.stringify(recentAttempts));
    return true;
  } catch {
    return true; // Allow on error
  }
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result with strength and message
 */
export const validatePasswordStrength = (password) => {
  const result = {
    isValid: false,
    strength: 'weak',
    message: [],
  };

  if (password.length < 8) {
    result.message.push('Password must be at least 8 characters long');
  }
  if (!/[A-Z]/.test(password)) {
    result.message.push('Password must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    result.message.push('Password must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    result.message.push('Password must contain at least one number');
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    result.message.push('Password must contain at least one special character');
  }

  if (result.message.length === 0) {
    result.isValid = true;
    result.strength = password.length >= 12 ? 'strong' : 'medium';
    result.message.push('Password is strong');
  }

  return result;
};

/**
 * Escape HTML to prevent XSS
 * @param {string} html - HTML string to escape
 * @returns {string} - Escaped HTML
 */
export const escapeHtml = (html) => {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
};

/**
 * Check if content contains suspicious patterns
 * @param {string} content - Content to check
 * @returns {boolean} - True if suspicious
 */
export const containsSuspiciousPattern = (content) => {
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // event handlers like onclick=
    /data:text\/html/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];

  return suspiciousPatterns.some(pattern => pattern.test(content));
};

/**
 * Sanitize filename
 * @param {string} filename - Filename to sanitize
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .substring(0, 255);
};
